import { Intercom, hide as intercomHide, show as intercomShow, showMessages as intercomShowMessages, showNewMessage as intercomShowNewMessage, shutdown as intercomShutdown, trackEvent as intercomTrackEvent, update as intercomUpdate } from '@intercom/messenger-js-sdk';
import React, { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { analytics } from '@/lib/analytics/analytics';
import { runWhenIdle } from '@/lib/scheduling/run-when-idle';
import { useAccountState } from '@/contexts/account/account-context';
import { useAuthToken } from '@/contexts/auth/auth-context';
import useStore from '@/store/app/use-store';
import { IntercomContext } from './intercom-context';
import { IntercomUserUpdate, IntercomEventMetadata, IntercomContextType } from './types/intercom-types';

interface IntercomProviderProps {
  children: React.ReactNode;
}

export function IntercomProvider({ children }: IntercomProviderProps) {
  const { user } = useStore(useShallow(s => ({ user: s.user })));
  const token = useAuthToken();
  const { currentAccount, accountId } = useAccountState();
  const intercomInitialized = useRef(false);
  const intercomUserId = useRef<string | null>(null);

  const intercomAppId = import.meta.env.VITE_APP_INTERCOM_APP_ID;

  const buildIntercomSettings = useCallback((): Parameters<typeof Intercom>[0] => {
    const userData: Parameters<typeof Intercom>[0] = {
      app_id: intercomAppId,
      // Custom launcher lives in PageTopbar / mobile More — hide the default
      // bottom-right messenger bubble so it does not compete with app chrome.
      hide_default_launcher: true,
    };

    if (user && token) {
      userData.user_id = user.sub;
      userData.email = user.email;
      userData.name =
        user.name || `${user.given_name || ''} ${user.family_name || ''}`.trim();
      userData.created_at = user['custom:created_at']
        ? Math.floor(new Date(user['custom:created_at']).getTime() / 1000)
        : undefined;

      if (currentAccount) {
        userData.company = {
          company_id: currentAccount.id,
          name: currentAccount.accountName,
          // @ts-expect-error - Subscription properties
          plan: currentAccount.subscription?.plan,
          // @ts-expect-error - Subscription properties
          monthly_spend: currentAccount.subscription?.monthlySpend,
        };

        // @ts-expect-error - Account properties
        userData.store_id = currentAccount.storeId;
        // @ts-expect-error - Account properties
        userData.store_name = currentAccount.storeName;
        // @ts-expect-error - Account properties
        userData.store_url = currentAccount.storeUrl;
        userData.pixel_ids = currentAccount.pixels?.map((p: { id: string }) => p.id).join(',');
        // @ts-expect-error - Account properties
        userData.integration_count = currentAccount.integrations?.length || 0;
        userData.account_id = accountId;
        userData.subscription_status = currentAccount.subscription?.status;
        userData.payment_gateway = currentAccount.subscription?.paymentGateway;
      }
    }

    return userData;
  }, [intercomAppId, user, token, currentAccount, accountId]);

  const boot = useCallback(() => {
    if (!intercomAppId || intercomInitialized.current) return;

    // This only initializes the SDK. Analytics identification is handled
    // separately by identifyCurrentUser() after login.
    const userData = buildIntercomSettings();
    Intercom(userData);
    intercomInitialized.current = true;
    intercomUserId.current = user?.sub ?? null;
  }, [intercomAppId, buildIntercomSettings, user?.sub]);

  const shutdown = useCallback(() => {
    if (!intercomInitialized.current) return;
    // Shutdown Intercom directly (don't use analytics.reset() to avoid double shutdown)
    intercomShutdown();
    // Reset custom-pixel and other providers (not Intercom to avoid double shutdown)
    // Use analytics service instead of direct window._upstack calls
    analytics.reset();
    intercomInitialized.current = false;
    intercomUserId.current = null;
  }, []);

  const update = useCallback((data?: IntercomUserUpdate) => {
    if (!intercomInitialized.current) return;
    intercomUpdate({
      ...data,
      hide_default_launcher: true,
    });
  }, []);

  const show = useCallback(() => {
    if (!intercomInitialized.current) return false;
    intercomShow();
    return true;
  }, []);

  const hide = useCallback(() => {
    if (!intercomInitialized.current) return;
    intercomHide();
  }, []);

  const showMessages = useCallback(() => {
    if (!intercomInitialized.current) return;
    intercomShowMessages();
  }, []);

  const showNewMessage = useCallback((message?: string) => {
    if (!intercomInitialized.current) return;
    intercomShowNewMessage(message || '');
  }, []);

  const trackEvent = useCallback((eventName: string, metadata?: IntercomEventMetadata) => {
    // Track via Intercom directly (IntercomAnalyticsProvider is excluded to avoid duplicates)
    if (intercomInitialized.current) {
      intercomTrackEvent(eventName, metadata);
    }
    // Track via custom-pixel and other providers (excluding Intercom)
    // Use analytics service instead of direct window._upstack calls
    analytics.track(eventName, metadata);
  }, []);

  useEffect(() => {
    if (!intercomAppId) {
      console.warn('Intercom App ID not configured');
      return;
    }

    if (!user || !token) {
      shutdown();
      return;
    }

    if (intercomInitialized.current && intercomUserId.current !== user.sub) {
      shutdown();
    }

    if (intercomInitialized.current) {
      return;
    }

    // Defer Intercom messenger boot until the browser is idle so its script
    // loading does not block first paint.
    const idleTask = runWhenIdle(boot);

    return () => {
      idleTask.cancel();
    };
  }, [user, token, boot, intercomAppId, shutdown]);

  useEffect(() => {
    if (!intercomInitialized.current || !user || !token) return;
    intercomUpdate(buildIntercomSettings());
  }, [user, token, currentAccount, accountId, buildIntercomSettings]);

  useEffect(() => {
    return () => {
      shutdown();
    };
  }, [shutdown]);

  const contextValue: IntercomContextType = {
    boot,
    shutdown,
    update,
    show,
    hide,
    showMessages,
    showNewMessage,
    trackEvent,
  };

  return <IntercomContext value={contextValue}>{children}</IntercomContext>;
}
